import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { Stack } from '@latitude/layout';
import { Text } from '@latitude/text';
import { FieldError } from 'components/FieldError';
import { get } from 'lodash-es';
import { useMemo } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useFormContext, useFormState } from 'react-hook-form';

import type { FormArrayProps } from './types';

export const FormArray = <T extends FieldValues>({
  control,
  name,
  renderFields,
  isMoveable = false,
  emptyMessage,
  move,
  fields,
  ...restProps
}: FormArrayProps<T>) => {
  const context = useFormContext<T>();

  const { errors } = useFormState({
    control: control ?? context.control,
    exact: true,
  });
  const fieldError = get(errors, name);
  // If you just have an error in the top level field array it will just be error.message
  // However if you also have a sub error in the "fields" under the array the top level
  // errors will be in fieldError.root.message
  const fieldErrorMessage = useMemo(() => {
    if (fieldError?.message) {
      return fieldError.message;
    }

    if (fieldError?.root && 'message' in fieldError.root) {
      return fieldError?.root.message;
    }
  }, [fieldError]);

  return (
    <Stack gap={12} {...restProps}>
      {fields.length === 0 && emptyMessage ? (
        <Text className="mb-2 text-center" variant="bodyMedium">
          {emptyMessage}
        </Text>
      ) : null}
      {fields.map((field, index) => (
        <Stack key={field.id} direction="horizontal" align="center" gap={8}>
          {isMoveable ? (
            <Stack>
              <IconButton
                data-testid={`move-up-btn-${index}`}
                type="ghost"
                disabled={index === 0}
                onClick={() => move(index, index - 1)}
                htmlType="button"
                variant="neutral"
                size="small"
                aria-label="Move up"
                icon={<Icon name="chevron-left" direction="up" />}
              />
              <IconButton
                data-testid={`move-down-btn-${index}`}
                type="ghost"
                htmlType="button"
                disabled={index === fields.length - 1}
                onClick={() => move(index, index + 1)}
                variant="neutral"
                size="small"
                aria-label="Move down"
                icon={<Icon name="chevron-left" direction="down" />}
              />
            </Stack>
          ) : null}
          {renderFields(index)}
        </Stack>
      ))}
      {fieldErrorMessage ? (
        <FieldError errorMessage={fieldErrorMessage as string} />
      ) : null}
    </Stack>
  );
};
